import { gql } from "@apollo/client";
import { FormGroup } from "@blueprintjs/core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { Flex, HexInputGroup, Txt } from "../../hex-components";
import { TestSelectors } from "../../util/testSelectors";
import { OrgOrPublicAvatar } from "../share/OrgOrPublicAvatar";

import type { SsoLoginWorkspacesQuery } from "./GlobalSsoLogin.generated";
import { useSsoLoginWorkspacesLazyQuery } from "./GlobalSsoLogin.generated";
import { LoginOption } from "./LoginOption";
import { startSsoLogin } from "./startSsoLogin";

gql`
  query SsoLoginWorkspaces($email: String!) {
    ssoLoginWorkspaces(email: $email) {
      orgId
      displayName
      hasCustomAppIcon
    }
  }
`;

export interface GlobalSsoLoginProps {
  authQueryParams: Record<string, string>;
  className?: string;
  embedded: boolean;
  onComplete: () => void;
  onSubmit: () => void;
}

type Step = "email" | "picker";

type SsoWorkspace = SsoLoginWorkspacesQuery["ssoLoginWorkspaces"][number];

const getSsoLookupErrorMessage = (message: string | undefined): string => {
  if (message?.includes("Enter a valid email address.")) {
    return "Enter a valid email address.";
  }

  return "Something went wrong while looking up SSO workspaces. Try again.";
};

export const GlobalSsoLogin: React.ComponentType<GlobalSsoLoginProps> =
  React.memo(function GlobalSsoLogin({
    authQueryParams,
    className,
    embedded,
    onComplete,
    onSubmit: onLoginSubmit,
  }) {
    const [step, setStep] = useState<Step>("email");
    const [email, setEmail] = useState<string>("");
    const [error, setError] = useState<string | undefined>(undefined);
    const isMountedRef = useRef<boolean>(true);

    const [fetchWorkspaces, { data, loading }] =
      useSsoLoginWorkspacesLazyQuery();
    const workspaces = data?.ssoLoginWorkspaces ?? [];

    useEffect(() => {
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    const routeToWorkspace = useCallback(
      (workspace: SsoWorkspace) => {
        startSsoLogin({
          authQueryParams,
          embedded,
          onComplete,
          onSubmit: onLoginSubmit,
          orgId: workspace.orgId,
        });
      },
      [authQueryParams, embedded, onComplete, onLoginSubmit],
    );

    const updateEmail = useCallback(
      (evt: React.FormEvent<HTMLInputElement>) => {
        setEmail(evt.currentTarget.value);
      },
      [],
    );

    const onSubmitEmail = useCallback(
      async (evt: React.FormEvent<HTMLFormElement>) => {
        evt.preventDefault();
        if (loading) {
          return;
        }

        setError(undefined);

        let found: readonly SsoWorkspace[];
        try {
          const result = await fetchWorkspaces({ variables: { email } });
          if (!isMountedRef.current) {
            return;
          }

          if (result.error != null) {
            setError(getSsoLookupErrorMessage(result.error.message));
            return;
          }

          found = result.data?.ssoLoginWorkspaces ?? [];
        } catch (err) {
          if (isMountedRef.current) {
            setError(
              getSsoLookupErrorMessage(
                err instanceof Error ? err.message : undefined,
              ),
            );
          }
          return;
        }

        if (found.length === 0) {
          setError(
            "We couldn't find a workspace for that email. Contact your workspace admin if you think this is a mistake.",
          );
        } else if (found.length === 1 && found[0] != null) {
          routeToWorkspace(found[0]);
        } else {
          setStep("picker");
        }
      },
      [email, fetchWorkspaces, loading, routeToWorkspace],
    );

    if (step === "picker") {
      return (
        <Flex className={className} direction="column" gap={10}>
          <Txt fontColor="muted" fontSize="small">
            Choose a workspace to continue.
          </Txt>
          {workspaces.map((workspace) => (
            <LoginOption
              fillText={true}
              key={workspace.orgId}
              justifyContent="flex-start"
              onClick={() => routeToWorkspace(workspace)}
            >
              <Flex align="center" gap={10}>
                <OrgOrPublicAvatar
                  hasCustomAppIcon={workspace.hasCustomAppIcon}
                  orgId={workspace.orgId}
                  orgName={workspace.displayName}
                  size={28}
                />
                <Txt ellipsize={true} fontWeight="medium">
                  {workspace.displayName}
                </Txt>
              </Flex>
            </LoginOption>
          ))}
        </Flex>
      );
    }

    return (
      <Form className={className} onSubmit={onSubmitEmail}>
        <FormGroup label="Email" labelFor="sso-email" labelInfo="(required)">
          <HexInputGroup
            autoFocus={true}
            data-test={TestSelectors.USERNAME}
            id="sso-email"
            name="sso-email"
            placeholder="you@example.com"
            required={true}
            type="email"
            value={email}
            onChange={updateEmail}
          />
        </FormGroup>
        {error != null && <ErrorText fontSize="small">{error}</ErrorText>}
        <LoginOption disabled={loading} loading={loading} type="submit">
          Continue
        </LoginOption>
      </Form>
    );
  });

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 0;
`;

const ErrorText = styled(Txt)`
  color: ${({ theme }) => theme.intent.DANGER};
`;
